import { NetworkStatus, gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import * as React from 'react';

import { useQueryWithPastDataDeprecated } from '@/imports/ui/useQueryWithPastData';

import {
  MOVE_ITEMS_MODAL_LIBRARY_FOLDER_FRAGMENT,
  MoveItemsModal,
} from './MoveItemsModal';

const GET_LIBRARY_FOLDERS = gql`
  query BulkMoveLibraryLinksModal_libraryFolder($input: SingleLibraryFolderInputV2!) {
    libraryFolderV2(input: $input) {
      result {
        _id
        ...MoveItemsModal_LibraryFolderFragment
        subFolders {
          results {
            _id
            ...MoveItemsModal_LibraryFolderFragment
          }
        }
      }
    }
  }
  ${MOVE_ITEMS_MODAL_LIBRARY_FOLDER_FRAGMENT}
`;

const MOVE_LIBRARY_LINKS_AST = gql`
  mutation BulkMoveLibraryLinksModal_moveGroupLibraryLinks($input: MoveGroupLibraryLinksInput!) {
    moveGroupLibraryLinks(input: $input)
  }
`;

type IBulkMoveLibraryLinksModalProps = {
  open: boolean;
  onClose: () => void;
  rootFolderId: string;
  groupId: string;
  linkIds: string[];
  onAfterMove: () => void;
};

export const BulkMoveLibraryLinksModal: React.FC<IBulkMoveLibraryLinksModalProps> = ({
  open,
  onClose,
  rootFolderId,
  groupId,
  linkIds,
  onAfterMove,
}) => {
  const toast = useToast();
  const [currentFolderId, setCurrentFolderId] = React.useState<string>(rootFolderId);
  const { error, data, networkStatus } = useQueryWithPastDataDeprecated(
    GET_LIBRARY_FOLDERS,
    {
      variables: {
        input: {
          id: currentFolderId,
        },
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );
  const [moveLinksFunc, { loading: loadingSubmit }] = useMutation(MOVE_LIBRARY_LINKS_AST, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  React.useEffect(() => {
    setCurrentFolderId(rootFolderId);
  }, [rootFolderId]);

  const onSubmitMoveHandler = async (destinationFolderId: string): Promise<void> => {
    try {
      await moveLinksFunc({
        variables: {
          input: {
            groupId: groupId,
            linkIds: linkIds,
            toFolderId: destinationFolderId,
          },
        },
      });
      onAfterMove();
      onClose();
      toast({ status: 'success', description: 'Moved links.' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to move links.' });
    }
  };

  if (error) {
    throw new Error('error getting folders');
  }

  const currentFolder = data?.libraryFolderV2?.result ? data.libraryFolderV2.result : undefined;
  const subFolders = currentFolder ? currentFolder.subFolders.results : [];
  const loadingFolders =
    networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables;

  return (
    <MoveItemsModal
      subFolders={subFolders}
      open={open}
      rootFolderId={rootFolderId}
      currentFolder={currentFolder}
      loadingFolders={loadingFolders}
      loadingSubmit={loadingSubmit}
      onClickCancel={onClose}
      onClickNavigateToFolder={(folderId) => setCurrentFolderId(folderId)}
      onClickSubmitMove={onSubmitMoveHandler}
      onClose={onClose}
    />
  );
};




